import {
	TRAVEL_INSURANCE_FAQS,
	TRAVEL_INSURANCE_HEADER_IMAGE,
	TRAVEL_INSURANCE_PROVIDERS,
} from "@/data/consts";
import * as React from "react";
import Image from "next/image";
import FAQs from "@/components/FAQs";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import { getOne } from "@/api/contentful";
import { Providers } from "@/types/providers";
import { createDealClick } from "@/api/tracking";
import PageSEO from "@/components/Shared/PageSEO";
import { UtmContext } from "@/context/utmContext";
import { KeywordContext } from "@/context/keyword";
import { FAQS } from "@/types/index";
import { HeaderImage as HeaderImageType } from "@/types/header";
import InsuranceCards from "@/components/Insurance/InsuranceCardList";
import InsuranceBenefits from "@/components/Insurance/InsuranceBenefits";
import WeWorkWithInsurance from "@/components/Insurance/WeWorkWithInsurance";
import InsuranceHeaderContent from "@/components/Header/InsuranceHeaderContent";

const Breadcrumbs = [
	{
		title: "Home",
		href: "/",
		active: false,
	},
	{
		title: "Insurance",
		href: "/insurance",
		active: false,
	},
	{
		title: "Travel Insurance",
		href: "/travel-insurance",
		active: true,
	},
];

const headerContentListItems = [
	"Compare up to 30 providers",
	"See if you can find a better travel insurance deal",
];

const insuranceBenefitsListItems = [
	{
		heading: "Time",
		text: "Quickly and easily compare insurance in one place",
	},
	{
		heading: "Variety",
		text: "Get access to various insurance providers.",
	},

	{
		heading: "Free",
		text: "The comparison site is completely free to use.",
	},
];

const insuranceHref =
	"https://insurance.savely.co.uk/travel/index.php?id=64394a5915073aeda43a2395977b1405";

const TravelInsurance: React.FC<{
	headerImage: HeaderImageType;
	FAQsContent: FAQS;
	providers: {
		fields: {
			providerLogos: Array<Providers>;
		};
	};
}> = ({ headerImage, FAQsContent, providers }) => {
	const { utmContent, utmCampaign, utmMedium, utmSource, utmTerm } =
		React.useContext(UtmContext);
	const { sclid, gclid, fbclid, msclkid, tbclid, obclid } =
		React.useContext(KeywordContext);

	const onDealClick = async () => {
		await createDealClick({
			type: "travel-insurance",
			click_reference:
				sclid ?? gclid ?? fbclid ?? msclkid ?? tbclid ?? obclid ?? utmContent,
			utm_campaign: utmCampaign,
			utm_content: utmContent,
			utm_source: utmSource,
			utm_medium: utmMedium,
			utm_term: utmTerm,
			product: "Travel Insurance",
			provider: "N/A",
			metadata: {},
		});
	};

	function generateInsuranceHref(): string {
		let href = insuranceHref;
		if (sclid || gclid || fbclid || msclkid || tbclid || obclid || utmContent) {
			href +=
				"&kw=" +
				(sclid || gclid || fbclid || msclkid || tbclid || obclid || utmContent);
		}
		return href;
	}

	return (
		<div className="h-100 d-flex flex-column">
			<PageSEO
				title={
					"Easily Compare Travel Insurance Options, Save Money and Travel with Peace of Mind | Consumer Choices"
				}
				description={
					"Compare travel insurance policies from top providers and find the best coverage and rates for your next trip. Protect yourself from unexpected travel disruptions and medical emergencies. Use Consumer Choices comparison website to save time and money."
				}
				keywords={"Travel Insurance, Travel Insurance Comparison"}
			/>
			<Header
				left={
					<InsuranceHeaderContent
						title={"Compare Travel Insurance Quotes"}
						listItems={headerContentListItems}
						insuranceLink={generateInsuranceHref()}
						onClick={onDealClick}
					/>
				}
				right={
					<Image
						src={"https:" + headerImage.fields.image?.fields.file?.url}
						alt={headerImage.fields.image?.fields.title ?? ""}
						height="450"
						width="600"
					/>
				}
				theme={{
					paddingTop: 5,
					paddingRight: 0,
					paddingLeft: 0,
					paddingBottom: 0,
				}}
				breadcrumbs={Breadcrumbs}
			/>

			<InsuranceBenefits
				headingTitle={"Looking for travel insurance?"}
				benefits={insuranceBenefitsListItems}
			/>
			<WeWorkWithInsurance providers={providers.fields.providerLogos} />
			<InsuranceCards title={"Related Insurance"} />
			<FAQs data={FAQsContent} />
			<section className={` pt-5 pb-5 text-center bg-blue`}>
				<div className="container-fluid pt-5 pb-5">
					<InsuranceHeaderContent
						title={"Compare Travel Insurance Quotes"}
						listItems={headerContentListItems}
						insuranceLink={
							insuranceHref +
							(sclid ||
							gclid ||
							fbclid ||
							msclkid ||
							tbclid ||
							obclid ||
							utmContent
								? "&kw=" +
								  (sclid ||
										gclid ||
										fbclid ||
										msclkid ||
										tbclid ||
										obclid ||
										utmContent)
								: "")
						}
						bottomHeader={true}
						onClick={onDealClick}
					/>
				</div>
			</section>
			<Footer />
		</div>
	);
};

export async function getStaticProps() {
	const FAQsContent = await getOne(TRAVEL_INSURANCE_FAQS);
	const providers = await getOne(TRAVEL_INSURANCE_PROVIDERS);
	const headerImage = await getOne(TRAVEL_INSURANCE_HEADER_IMAGE);

	return {
		props: {
			headerImage,
			FAQsContent,
			providers,
		},
		revalidate: Number(process.env.REVALIDATE_CONTENTFUL),
	};
}

export default TravelInsurance;
